import React, { useEffect, useState } from 'react';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import SearchInput from '../../../commons/SearchInput';
import { ContFilterYear, ContPaginationPage } from '../../../styled-components';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import { useAuth } from '../../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import axios from 'axios';
import AgreementsTable from './AgreementsTable';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const SupplierCondoAgreementsPresentational = ({
  handleFileSigned,
  handleFileListView,
}) => {
  const [{ newToken, profile = {} } = {}] = useAuth();
  const [t] = useTranslation('AGREEMENTS');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [agreements, setAgreements] = useState({});
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    name: withDefault(StringParam, ''),
    admin: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    vatCode: withDefault(StringParam, ''),
    status: withDefault(StringParam, ''),
    condo: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    name,
    admin,
    supplier,
    vatCode,
    status,
    condo,
  } = query;

  // Fetch Contratti
  const fetchAgreements = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agreements`,
      {
        headers: { Authorization: newToken },
        params: { ...query, agreementType: 'SUPPLIER_CONDO' },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setAgreements(data.agreements);
    setTotal(+headers['x-total-count']);
  };

  function onFilter(queryDiff) {
    if (
      (queryDiff.name && queryDiff.name !== name) || // first search
      (name && queryDiff.name === '') || // search deleted
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.status && queryDiff.status !== status) || // first search
      (status && queryDiff.status === '') || // search deleted
      (queryDiff.vatCode && queryDiff.vatCode !== vatCode) || // first search
      (vatCode && queryDiff.vatCode === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  useEffect(() => {
    fetchAgreements();
  }, [limit, offset, name, admin, supplier, vatCode, status, condo]);

  return (
    <>
      <Row className="search-add-cont m-t-20 d-flex" style={{ rowGap: '10px' }}>
        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.admin }}
            onFilter={({ search }) => onFilter({ admin: search })}
            label={t('SEARCH:BY_ADMIN')}
          />
        </Col>

        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.supplier }}
            onFilter={({ search }) => onFilter({ supplier: search })}
            label={t('SEARCH:BY_SUPPLIER')}
          />
        </Col>

        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.name }}
            onFilter={({ search }) => onFilter({ name: search })}
            label={t('SEARCH:NAME_AGREEMENT')}
          />
        </Col>

        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.condo }}
            onFilter={({ search }) => onFilter({ condo: search })}
            label={t('SEARCH:CONDOMINIUM')}
          />
        </Col>

        <Col sm="12" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.vatCode }}
            onFilter={({ search }) => onFilter({ vatCode: search })}
            label={t('SEARCH:CONDOMINIUM_VAT_CODE')}
          />
        </Col>

        <Col sm="12" md="6" lg="3" className="select-wrapper">
          <ContFilterYear className="no-width">
            <Row>
              <Col xs="6">
                <Label>Stato contratto</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="status"
                    name="status"
                    onChange={(e) =>
                      onFilter({
                        status: e.target.value,
                      })
                    }
                    value={query.status || ''}
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    {profile.type !== 'SUPPLIER' && (
                      <option value="DRAFT">Bozza</option>
                    )}
                    {profile.type !== 'SUPPLIER' && (
                      <option value="COMPILATION">Compilazione</option>
                    )}
                    <option value="REVISION">Revisione</option>
                    <option value="REQUESTED">Richiesta di firma</option>
                    <option value="SIGNED">Firmato</option>
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>
      </Row>

      <section style={{ marginTop: '10px' }}>
        <AgreementsTable
          data={agreements}
          total={total}
          offset={offset}
          limit={limit}
          onFilter={onFilter}
          profile={profile}
          handleFileSigned={handleFileSigned}
          handleFileListView={handleFileListView}
          fetchAgreements={fetchAgreements}
        />

        <ContPaginationPage>
          <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

          <FormGroup>
            <CustomInput
              type="select"
              className="pagination-select"
              id="perPage"
              name="perPage"
              value={query.limit}
              onChange={({ target: { value: limit } }) => {
                onFilter({ limit });
              }}
            >
              {generatePaginationOptions()}
            </CustomInput>
          </FormGroup>
        </ContPaginationPage>
      </section>
    </>
  );
};

export default SupplierCondoAgreementsPresentational;
